/*eslint-disable*/
import cloneDeep from 'lodash/cloneDeep';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MaskedInput from 'react-text-mask';
import emailMask from 'text-mask-addons/dist/emailMask';
import { log } from '../../Log.init'

// @mui/material components
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { makeStyles } from '@mui/styles';

// material ui icons
import BusinessIcon from '@mui/icons-material/Business';
import Edit from '@mui/icons-material/Edit';
import Email from '@mui/icons-material/Email';
import Environment from '@mui/icons-material/SettingsInputComponent';
import WysiwygIcon from '@mui/icons-material/Wysiwyg';

// core components
import CustomInput from 'components/CustomInput/CustomInput.js';
import ReadOnly from 'components/CustomInput/ReadOnly.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';
import DatePicker from 'components/CustomInput/CalendarInput.js';
import ImageUpload from 'components/CustomUpload/ImageUpload.js';
import Table from 'components/Table/Table.js';

import ClientTenant from './ClientTenant.js';
import FeatureLibrary from './FeatureLibrary/index.js';
import { uploadImageFile } from '../../api/imageAPI';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/clientEditStyle.js';

// Redux
import { Box } from '@mui/material';
import {
  useGetClientTenantsQuery,
  useUpdateClientMutation,
} from 'api/client.api.js';
import { selectViewIsDirty, setViewIsDirty } from 'features/ux/uxSlice';
import { selectCurrentClient } from '../../features/client/clientSlice';
import { isValidEmail } from '../../utils';

const useStyles = makeStyles(styles);

const phoneMaskProps = {
  guide: false,
  placeholderChar: '\u2000',
  mask: [
    '(',
    /[1-9]/,
    /\d/,
    /\d/,
    ')',
    ' ',
    /\d/,
    /\d/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    /\d/,
    /\d/,
  ],
};

function phoneMaskCustom(props) {
  let { inputRef, ...other } = props;

  if (typeof inputRef !== 'function') {
    inputRef = () => {};
  }

  return (
    <MaskedInput
      {...other}
      {...phoneMaskProps}
      ref={(ref) => {
        try {
          inputRef(ref ? ref.inputElement : null);
        } catch (e) {
          log.error({message: `Input error: ${e.message}`});
        }
      }}
    />
  );
}

const emailMaskProps = {
  guide: false,
  placeholderChar: '\u2000',
  mask: emailMask,
};

const emailMaskCustom = (props) => {
  let { inputRef, ...other } = props;

  if (typeof inputRef !== 'function') {
    inputRef = () => {};
  }

  return (
    <MaskedInput
      {...other}
      {...emailMaskProps}
      ref={(ref) => {
        try {
          inputRef(ref ? ref.inputElement : null);
        } catch (e) {
             log.error({message: `Input error: ${e.message}`});
        }
      }}
    />
  );
};

export default function Client(props) {
  const dispatch = useDispatch();
  const client = useSelector(selectCurrentClient);
  const viewIsDirty = useSelector(selectViewIsDirty);
  const classes = useStyles();
  const [updateClient] = useUpdateClientMutation();
  const { data: clientTenants } = useGetClientTenantsQuery(client._id);

  const [logo, setLogo] = useState(client.logo);
  const [logoFile, setLogoFile] = useState(null);
  const [logoError, setLogoError] = useState('');
  
  const [name, setName] = useState(client.name);
  const [nameState, setNameState] = useState('');
  const [nameError, setNameError] = useState('');
  const [description, setDescription] = useState(client.description);
  const [descriptionState, setDescriptionState] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [startDate, setStartDate] = useState(client.start_date); // startDate is display only
  const [endDate, setEndDate] = useState(client.end_date);
  const [endDateState, setEndDateState] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [status, setStatus] = useState(client.status);
  const [statusState, setStatusState] = useState('');
  const [statusError, setStatusError] = useState('');
  const [billingName, setBillingName] = useState(
    client.billing_contact.full_name
  );
  const [billingNameState, setBillingNameState] = useState('');
  const [billingNameError, setBillingNameError] = useState('');
  const [billingPhone, setBillingPhone] = useState(
    client.billing_contact.phone_number
  );
  const [billingPhoneState, setBillingPhoneState] = useState('');
  const [billingPhoneError, setBillingPhoneError] = useState('');
  const [billingEmail, setBillingEmail] = useState(
    client.billing_contact.email_address
  );
  const [billingEmailState, setBillingEmailState] = useState('');
  const [billingEmailError, setBillingEmailError] = useState('');
  const [administratorName, setAdministratorName] = useState(
    client.administrative_contact.full_name
  );
  const [administratorNameState, setAdministratorNameState] = useState('');
  const [administratorNameError, setAdministratorNameError] = useState('');
  const [administratorPhone, setAdministratorPhone] = useState(
    client.administrative_contact.phone_number
  );
  const [administratorPhoneState, setAdministratorPhoneState] = useState('');
  const [administratorPhoneError, setAdministratorPhoneError] = useState('');
  const [administratorEmail, setAdministratorEmail] = useState(
    client.administrative_contact.email_address
  );
  const [administratorEmailState, setAdministratorEmailState] = useState('');
  const [administratorEmailError, setAdministratorEmailError] = useState('');

  const checkDirtyForm = (value, original, length = 0, required = false) => {
    if (
      value !== original &&
      ((required && value.length >= length) || !required)
    ) {
      dispatch(setViewIsDirty(true));
    } else {
      dispatch(setViewIsDirty(false));
    }
  };

  const isValidForm = () => {
    return (
      nameError == '' &&
      descriptionError == '' &&
      billingNameError == '' &&
      billingPhoneError == '' &&
      billingEmailError == '' &&
      administratorNameError == '' &&
      administratorPhoneError == '' &&
      administratorEmailError == ''
    );
  };

  // function that returns true if value is email, false otherwise
  const verifyEmail = (value, original) => {
    if (isValidEmail(value)) {
      checkDirtyForm(value, original);
      return true;
    }
    return false;
  };

  // function that verifies if a string has a given length or not
  const verifyLength = (value, original, length, required = true) => {
    checkDirtyForm(value, original, length, required);
    // if (value !== original && (required && value.length >= length)) {
    //   dispatch(setViewIsDirty(true));
    // }
    // else{
    //   dispatch(setViewIsDirty(false));
    // }

    if ((value && value.length >= length) || !required) {
      return true;
    }
    return false;
  };

  const changeClick = async () => {
    var error = false;
    var updatedClient = cloneDeep(client);

    if (name === '' && nameState === '') {
      error = true;
      setNameState('error');
    }
    if (description === '' && descriptionState === '') {
      error = true;
      setDescriptionState('error');
    }
    if (endDate === '' && endDateState === '') {
      error = true;
      setEndDateState('error');
    }
    if (status === '' && statusState === '') {
      error = true;
      setStatusState('error');
    }
    if (billingName === '' && billingNameState === '') {
      error = true;
      setBillingNameState('error');
    }
    if (billingPhone === '' && billingPhoneState === '') {
      error = true;
      setBillingPhoneState('error');
    }
    if (billingEmail === '' && billingEmailState === '') {
      error = true;
      setBillingEmailState('error');
    }
    if (administratorName === '' && administratorNameState === '') {
      error = true;
      setAdministratorNameState('error');
    }
    if (administratorPhone === '' && administratorPhoneState === '') {
      error = true;
      setAdministratorPhoneState('error');
    }
    if (administratorEmail === '' && administratorEmailState === '') {
      error = true;
      setAdministratorEmailState('error');
    }

    if (logoFile) {
      const logo_url = await uploadImageFile(client._id, logoFile);
      if (logo_url === '') {
        setLogoError('There was an error uploading your logo');
        error = true;
      }
      setLogo(logo_url)
      updatedClient.logo = logo_url;
    }
    else {
      updatedClient.logo = logo;
    }
    
    if (error === false) {
      updatedClient.name = name;
      updatedClient.description = description;
      updatedClient.end_date = endDate;
      updatedClient.status = status;
      updatedClient.billing_contact.full_name = billingName;
      updatedClient.billing_contact.phone_number = billingPhone;
      updatedClient.billing_contact.email_address = billingEmail;
      updatedClient.administrative_contact.full_name = administratorName;
      updatedClient.administrative_contact.phone_number = administratorPhone;
      updatedClient.administrative_contact.email_address = administratorEmail;

      updateClient(updatedClient).unwrap();
      dispatch(setViewIsDirty(false));
    }
  };
  const statusChangeHandler = (event) => {
    dispatch(setViewIsDirty(true));
    setStatus(event.target.value);
    setStatusState('success');
  };

  const [editEnvironment, setEditEnvironment] = React.useState(false);
  const [addEnvironment, setAddEnvironment] = React.useState(false);
  const [currentEnvironment, setCurrentEnvironment] = React.useState(null);

  const [dirtyData, setDirtyData] = useState(false);

  const handleEditEnvironment = (index) => {
    dispatch(setViewIsDirty(true));
    setCurrentEnvironment(clientTenants[index]);
    setEditEnvironment(true);
  };

  const handleCloseEnvironment = () => {
    setEditEnvironment(false);
    setAddEnvironment(false);
  };

  const emailAdministrator = (index) => {
    window.location.href = `mailto:${clientTenants[index].administrator_email}`;
  };

  const logViewHandler = (data) => {
    props.history.push('/admin/logs', data);
  };

  const actionButton = (Icon, color, handler, key) => {
    return (
      <Button
        color={color}
        simple
        className={classes.actionButton}
        onClick={() => handler(key)}
      >
        <Icon className={classes.icon} />
      </Button>
    );
  };

  const addEnvironmentHandler = () => {
    setAddEnvironment(true);
  };

  const environments = () => {
    const environmentList = clientTenants.map((tenant, key) => {
      var clientTenant = [];
      var actionButtons = [];
      clientTenant.push(tenant?.tenant_label);
      clientTenant.push(tenant?.tenant_id);
      clientTenant.push(tenant?.status);
      clientTenant.push(tenant?.production === true ? 'Production' : 'Test');
      clientTenant.push(tenant?.administrator_email);
      actionButtons.push(
        actionButton(Edit, 'warning', handleEditEnvironment, key)
      );
      actionButtons.push(
        actionButton(Email, 'warning', emailAdministrator, key)
      );
      actionButtons.push(
        actionButton(WysiwygIcon, 'warning', logViewHandler, { tenant, client })
      );
      clientTenant.push(actionButtons);
      return clientTenant;
    });
    return environmentList;
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <BusinessIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Company Information</h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={6} sm={4} md={3}>
                <ImageUpload
                  image={logo}
                  getImageFile={(image) => setLogoFile(image)}
                  onRemoveImageFile={() => {
                    setLogo('');
                    setLogoFile(null);
                  }}
                />
                { logoError !== '' ? (
                    <p className={classes.textDanger}>
                      { logoError }
                    </p>
                  ) : null }
              </GridItem>
              <GridItem xs={6} sm={8} md={9}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100%',
                  }}
                >
                  <Box>
                    <CustomInput
                      helperText={
                        nameState === 'error'
                          ? nameError ?? 'An error has occurred'
                          : false
                      }
                      success={nameState === 'success'}
                      error={nameState === 'error'}
                      labelText='Company Name'
                      id='name'
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        defaultValue: name,
                        minLength: 3,
                        onChange: (event) => {
                          if (verifyLength(event.target.value, name, 3)) {
                            setNameState('success');
                            setNameError('');
                          } else {
                            setNameState('error');
                            setNameError(
                              'The field should be at least 3 characters long'
                            );
                          }
                          setName(event.target.value);
                        },
                        autoComplete: 'off',
                      }}
                    />
                    <CustomInput
                      helperText={
                        descriptionState === 'error'
                          ? descriptionError ?? 'An error has occurred'
                          : false
                      }
                      success={descriptionState === 'success'}
                      error={descriptionState === 'error'}
                      labelText='Company Description'
                      id='description'
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        defaultValue: description,
                        onChange: (event) => {
                          if (
                            verifyLength(event.target.value, description, 3)
                          ) {
                            setDescriptionState('success');
                            setDescriptionError('');
                          } else {
                            setDescriptionState('error');
                            setDescriptionError(
                              'The field should be at least 3 characters long'
                            );
                          }
                          setDescription(event.target.value);
                        },
                        autoComplete: 'off',
                      }}
                    />
                    <ReadOnly
                      labelText='Start Date'
                      id='startdate'
                      formControlProps={{
                        fullWidth: true,
                      }}
                      displayValue={new Date(startDate).toDateString()}
                      inputProps={{
                        readOnly: true,
                        defaultValue: new Date(startDate).toDateString(),
                        autoComplete: 'off',
                      }}
                    />
                    <DatePicker
                      isValidDate={(current) => {
                        return startDate ? current.isAfter(startDate) : true;
                      }}
                      success={endDateState === 'success'}
                      error={endDateState === 'error'}
                      labelText='End Date as'
                      id='enddate'
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        value: endDate ? new Date(endDate).toDateString() : '',
                        onChange: (event) => {
                          dispatch(setViewIsDirty(true));
                          setEndDateState('success');
                          setEndDate(event._d);
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                    }}
                  >
                    <FormControl
                      variant='standard'
                      className={classes.formControl}
                    >
                      <InputLabel
                        id='status-select-label'
                        className={classes.inputLabel}
                      >
                        Status
                      </InputLabel>
                      <Select
                        variant='standard'
                        className={classes.select}
                        labelId='status-select-label'
                        id='status-select'
                        value={status}
                        onChange={(event) => statusChangeHandler(event)}
                      >
                        <MenuItem value='active'>Active</MenuItem>
                        <MenuItem value='inactive'>Inactive</MenuItem>
                        <MenuItem value='deleted'>Delete</MenuItem>
                      </Select>
                    </FormControl>
                    <Box>
                      <Button
                        color='rose'
                        onClick={changeClick}
                        className={classes.registerButton}
                        disabled={!viewIsDirty || !isValidForm()}
                      >
                        Save Changes
                      </Button>
                      <Button
                        color='secondary'
                        onClick={() => props.history.push('/admin/companies')}
                        className={'mr-2 ' + classes.registerButton}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem xs={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <Environment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Environments</h4>
            <Button
              color='rose'
              onClick={addEnvironmentHandler}
              className={classes.registerButton}
            >
              + Add Environment
            </Button>
          </CardHeader>
          <CardBody>
            <Table
              tableHead={[
                'Environment',
                'Tenant ID',
                'Status',
                'Type',
                'Administrator',
                'Actions',
              ]}
              tableData={environments()}
            />
          </CardBody>
        </Card>
      </GridItem>
      {editEnvironment === true ? (
        <ClientTenant
          open={editEnvironment}
          handleClose={handleCloseEnvironment}
          tenant={currentEnvironment}
        />
      ) : null}
      {addEnvironment === true ? (
        <ClientTenant
          new
          clientId={client._id}
          tenantNumber={clientTenants.length + 1}
          open={addEnvironment}
          handleClose={handleCloseEnvironment}
        />
      ) : null}
      <FeatureLibrary />
    </GridContainer>
  );
  //
}
